import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginated } from '@/core/api';

import * as api from './api';
import type {
  CreateProjectLinkArtifactInput,
  GradeProjectInput,
  ProjectListParams,
  ProjectSubmissionListParams,
  ProjectTaskDto,
  ProjectTaskWriteInput,
  ProjectWriteInput,
} from './types';

/**
 * The board is read whole: reordering requires posting every task of the
 * project, so a paginated board would be unusable. 200 covers any realistic
 * class project and the server's page cap.
 */
export const BOARD_PAGE_SIZE = 200;

/** Query-key factories — every projects key derives from these. */
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (params: ProjectListParams) => [...projectKeys.lists(), params] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...projectKeys.details(), publicId] as const,
};

export const projectTaskKeys = {
  all: ['project-tasks'] as const,
  boards: () => [...projectTaskKeys.all, 'board'] as const,
  board: (projectId: string) => [...projectTaskKeys.boards(), projectId] as const,
};

export const taskCommentKeys = {
  all: ['task-comments'] as const,
  lists: () => [...taskCommentKeys.all, 'list'] as const,
  list: (taskId: string) => [...taskCommentKeys.lists(), taskId] as const,
};

export const projectSubmissionKeys = {
  all: ['project-submissions'] as const,
  lists: () => [...projectSubmissionKeys.all, 'list'] as const,
  list: (params: ProjectSubmissionListParams) =>
    [...projectSubmissionKeys.lists(), params] as const,
  /** The team's shared deliverable for one project (get-or-create read). */
  forProject: (projectId: string) => [...projectSubmissionKeys.all, 'project', projectId] as const,
};

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export function useProjects(params: ProjectListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: () => api.getProjects(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useProject(publicId: string) {
  return useQuery({
    queryKey: projectKeys.detail(publicId),
    queryFn: () => api.getProject(publicId),
    enabled: Boolean(publicId),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectWriteInput) => api.createProject(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: projectKeys.lists() }),
  });
}

export function useUpdateProject(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectWriteInput) => api.updateProject(publicId, input),
    // PATCH answers with the write shape (bare `school_class`, un-enriched
    // blocks) — priming the detail cache with it would break the renderer.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.detail(publicId) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/** publish / unpublish / archive all answer with the detail payload. */
function useProjectLifecycleMutation(fn: (publicId: string) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => fn(publicId),
    onSuccess: (_detail, publicId) => {
      void qc.invalidateQueries({ queryKey: projectKeys.detail(publicId) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

export function usePublishProject() {
  return useProjectLifecycleMutation(api.publishProject);
}

export function useUnpublishProject() {
  return useProjectLifecycleMutation(api.unpublishProject);
}

export function useArchiveProject() {
  return useProjectLifecycleMutation(api.archiveProject);
}

export function useDuplicateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, schoolClass }: { publicId: string; schoolClass?: string }) =>
      api.duplicateProject(publicId, schoolClass),
    onSuccess: copy => {
      qc.setQueryData(projectKeys.detail(copy.public_id), copy);
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/**
 * Replaces the WHOLE team in one call (backend ADR-017). Removing a member
 * unassigns their board cards server-side, so the board is invalidated too.
 */
export function useReplaceProjectMembers(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (members: string[]) => api.replaceProjectMembers(publicId, members),
    onSuccess: detail => {
      qc.setQueryData(projectKeys.detail(publicId), detail);
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
      void qc.invalidateQueries({ queryKey: projectTaskKeys.board(publicId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Task board — optimistic, with rollback
// ---------------------------------------------------------------------------

export function useProjectTasks(projectId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: projectTaskKeys.board(projectId),
    queryFn: () => api.getProjectTasks({ project: projectId, page_size: BOARD_PAGE_SIZE }),
    enabled: (options.enabled ?? true) && Boolean(projectId),
  });
}

type BoardPage = Paginated<ProjectTaskDto>;

/** Shared optimistic scaffolding: snapshot the board, apply `patch`, roll the
 *  snapshot back on failure, re-sync from the server either way. */
function useOptimisticBoardMutation<TVars>(
  projectId: string,
  mutationFn: (vars: TVars) => Promise<unknown>,
  patch: (board: BoardPage, vars: TVars) => BoardPage
) {
  const qc = useQueryClient();
  const key = projectTaskKeys.board(projectId);
  return useMutation({
    mutationFn,
    onMutate: async (vars: TVars) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<BoardPage>(key);
      if (previous) qc.setQueryData<BoardPage>(key, patch(previous, vars));
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  });
}

function sortBoard(results: ProjectTaskDto[]): ProjectTaskDto[] {
  return [...results].sort((a, b) => a.order - b.order);
}

export function useCreateProjectTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectTaskWriteInput) =>
      api.createProjectTask({ ...input, project: projectId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: projectTaskKeys.board(projectId) }),
  });
}

/** Moving a card between columns, assigning it, editing it — all optimistic. */
export function useUpdateProjectTask(projectId: string) {
  return useOptimisticBoardMutation<{ publicId: string; input: ProjectTaskWriteInput }>(
    projectId,
    ({ publicId, input }) => api.updateProjectTask(publicId, input),
    (board, { publicId, input }) => ({
      ...board,
      results: board.results.map(task =>
        task.public_id === publicId
          ? {
              ...task,
              ...(input.title !== undefined ? { title: input.title } : {}),
              ...(input.description !== undefined ? { description: input.description } : {}),
              ...(input.status !== undefined ? { status: input.status } : {}),
              ...(input.due_at !== undefined ? { due_at: input.due_at } : {}),
            }
          : task
      ),
    })
  );
}

export function useDeleteProjectTask(projectId: string) {
  return useOptimisticBoardMutation<string>(
    projectId,
    publicId => api.deleteProjectTask(publicId),
    (board, publicId) => ({
      ...board,
      count: Math.max(0, board.count - 1),
      results: board.results.filter(task => task.public_id !== publicId),
    })
  );
}

/**
 * Reorder within a column. The API demands the WHOLE board in the new order,
 * so the caller assembles every task id — a partial list is rejected.
 */
export function useReorderProjectTasks(projectId: string) {
  return useOptimisticBoardMutation<string[]>(
    projectId,
    tasks => api.reorderProjectTasks({ project: projectId, tasks }),
    (board, tasks) => {
      const position = new Map(tasks.map((id, index) => [id, index + 1]));
      return {
        ...board,
        results: sortBoard(
          board.results.map(task => ({
            ...task,
            order: position.get(task.public_id) ?? task.order,
          }))
        ),
      };
    }
  );
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export function useTaskComments(taskId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskCommentKeys.list(taskId),
    queryFn: () => api.getTaskComments({ task: taskId, page_size: 100 }),
    enabled: (options.enabled ?? true) && Boolean(taskId),
  });
}

export function useCreateTaskComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.createTaskComment(taskId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: taskCommentKeys.list(taskId) }),
  });
}

export function useUpdateTaskComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, body }: { publicId: string; body: string }) =>
      api.updateTaskComment(publicId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: taskCommentKeys.list(taskId) }),
  });
}

export function useDeleteTaskComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteTaskComment(publicId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: taskCommentKeys.list(taskId) }),
  });
}

// ---------------------------------------------------------------------------
// The team deliverable
// ---------------------------------------------------------------------------

export function useProjectSubmissions(
  params: ProjectSubmissionListParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: projectSubmissionKeys.list(params),
    queryFn: () => api.getProjectSubmissions(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

/**
 * The team's shared deliverable, read the way the caller is allowed to.
 *
 * The backend exposes no GET for "the result of this project". For a TEAM
 * MEMBER, `POST /project-submissions/` is an explicit get-or-create (200 when
 * it already existed), so it doubles as the read — the join-code shape of
 * FE-ADR-010. Staff are NOT team members, so that call 400s
 * (`project_not_available`) for them; they read the single row off the list
 * endpoint, which is empty until a pupil opens the result.
 *
 * Both paths cache under the same key, so every mutation below refreshes the
 * panel whoever is looking at it.
 */
export function useProjectSubmission(
  projectId: string,
  options: { enabled?: boolean; asTeamMember?: boolean } = {}
) {
  const asTeamMember = options.asTeamMember ?? true;
  return useQuery({
    queryKey: projectSubmissionKeys.forProject(projectId),
    queryFn: async () => {
      if (asTeamMember) return api.openProjectSubmission(projectId);
      const page = await api.getProjectSubmissions({ project: projectId, page_size: 1 });
      return page.results[0] ?? null;
    },
    enabled: (options.enabled ?? true) && Boolean(projectId),
    retry: false,
  });
}

export function useUpdateProjectSubmissionText(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, text }: { publicId: string; text: string }) =>
      api.updateProjectSubmissionText(publicId, text),
    onSuccess: submission => {
      qc.setQueryData(projectSubmissionKeys.forProject(projectId), submission);
      void qc.invalidateQueries({ queryKey: projectSubmissionKeys.lists() });
    },
  });
}

export function useSubmitProjectSubmission(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.submitProjectSubmission(publicId),
    onSuccess: submission => {
      qc.setQueryData(projectSubmissionKeys.forProject(projectId), submission);
      void qc.invalidateQueries({ queryKey: projectSubmissionKeys.lists() });
    },
  });
}

export function useGradeProjectSubmission(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: GradeProjectInput }) =>
      api.gradeProjectSubmission(publicId, input),
    onSuccess: submission => {
      qc.setQueryData(projectSubmissionKeys.forProject(projectId), submission);
      void qc.invalidateQueries({ queryKey: projectSubmissionKeys.lists() });
    },
  });
}

export function useReturnProjectSubmission(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, feedback }: { publicId: string; feedback: string }) =>
      api.returnProjectSubmission(publicId, feedback),
    onSuccess: submission => {
      qc.setQueryData(projectSubmissionKeys.forProject(projectId), submission);
      void qc.invalidateQueries({ queryKey: projectSubmissionKeys.lists() });
    },
  });
}

/** Artifact writes refresh the owning deliverable (artifacts are nested in its
 *  payload). Uploads are called imperatively so progress can be reported. */
export function useCreateProjectLinkArtifact(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectLinkArtifactInput) => api.createProjectLinkArtifact(input),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: projectSubmissionKeys.forProject(projectId) }),
  });
}

export function useDeleteProjectArtifact(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteProjectArtifact(publicId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectSubmissionKeys.forProject(projectId) });
      void qc.invalidateQueries({ queryKey: projectSubmissionKeys.lists() });
    },
  });
}
